/**
 * sc-pdp-configurator.js — Studio Couleur V1 Pilote
 * Moteur configurateur complet : Gamme → Finition → Forme (switch AJAX) → Taille → Coloris → Option (poignée, si Bois naturel + Bouton)
 * Toutes les étapes restent toujours dépliées ; chaque sélection (Gamme, Finition, Coloris,
 * Option si applicable) se ré-applique en cascade sur la 1ère valeur disponible de l'étape
 * suivante par défaut — y compris lors d'un changement en amont ("réinitialisation").
 * Filtrage client-side depuis le JSON island #sc-configurator-data (injecté en Liquid).
 */

(function () {
  'use strict';

  const DEBUG = new URLSearchParams(window.location.search).has('sc_debug');
  function log(...a) { if (DEBUG) console.log('[SC]', ...a); }

  // La finition tranche décor bois attend DEUX choix : une couleur de façade et
  // un décor de tranche. Elle conditionne l'étape Tranche et le message d'aide.
  function besoinTranche(s) {
    return s.gammeCode === 'couleur' && s.finitionCode === 'tranche-decor-bois-couleur';
  }

  const SC_CONFIG = {
    version: '3.4.0',
    gammeColors: {
      'couleur':       '#E07A5F',
      'decor-bois':    '#C9A882',
      'bois-naturel':  '#8B6347',
      'aspect-pierre': '#888480',
    },
    requiredFields: [
      { key: 'gamme',    label: 'Gamme',    validate: s => !!s.gammeCode,
        message: function() { return 'Merci de sélectionner une collection'; } },
      { key: 'finition', label: 'Finition', validate: s => !!s.finitionCode,
        message: function() { return 'Merci de sélectionner une finition'; } },
      // Débrief Agnès 21/09 : une seule et même phrase quelle que soit la
      // collection, puisque l'étape s'intitule « Couleur » même pour le décor
      // bois ou le bois naturel. Seule la finition tranche décor bois fait
      // exception : deux choix y sont attendus, d'où une phrase dédiée, et sans
      // « Merci de » en tête pour ne pas allonger la ligne.
      // Un pack d'échantillons contient tout : ni couleur ni tranche à choisir
      // (fiche Échantillons, cf. `sansCouleur` sur le variant du catalogue).
      { key: 'coloris',  label: 'Coloris',  validate: s => s.sansCouleur || !!s.colorisCode,
        message: function(s) {
          return besoinTranche(s)
            ? 'Sélectionnez une couleur et une tranche'
            : 'Merci de sélectionner une couleur';
        }
      },
      { key: 'option',   label: 'Emplacement', validate: s => !s.optionRequired || !!s.optionCode,
        message: function() { return 'Merci de sélectionner un emplacement de poignée'; } },
      { key: 'tranche',  label: 'Décor tranche', validate: s => !besoinTranche(s) || s.sansCouleur || !!s.trancheDecorCode,
        message: function() { return 'Sélectionnez une couleur et une tranche'; }
      },
    ],
    storagePrefix: 'sc_config_',
    // Message d'aide sous le sélecteur de taille (débrief Agnès 16/07) : sur la
    // forme Porte, les hauteurs 180 et 200 cm n'existent qu'en finition Pleine.
    // Le test porte sur le LIBELLÉ normalisé de la finition, pas sur son code :
    // les codes sont des handles de metaobjects qu'Agnès renomme régulièrement
    // (cf. scripts/set-variant-positions.py), les libellés restent stables.
    tailleHint: {
      formeCode: 'porte',
      finitionLabels: ['bulle', 'encoche', 'prise de main'],
      text: 'Pour les portes H.180 et H.200 cm, sélectionnez la finition Pleine',
    },
  };

  // Debug overlay
  const SCDebug = {
    panel: null,
    init(pid) {
      if (!DEBUG) return;
      if (this.panel) { this.panel.remove(); this.panel = null; }
      const p = document.createElement('div');
      p.id = 'sc-debug-panel';
      p.style.cssText = 'position:fixed;bottom:16px;right:16px;z-index:9999;background:rgba(0,0,0,0.88);color:#0f0;font:11px/1.5 monospace;padding:12px 16px;border-radius:8px;max-width:340px;border:1px solid #0f0;pointer-events:none';
      p.innerHTML = '<b>[SC v' + SC_CONFIG.version + ']</b><br><span id="sc-dbg-body">init...</span>';
      document.body.appendChild(p);
      this.panel = p;
    },
    update(state, result) {
      const b = this.panel && this.panel.querySelector('#sc-dbg-body');
      if (!b) return;
      b.innerHTML = [
        'Gamme    : ' + (state.gammeCode || '—'),
        'Finition : ' + (state.finitionCode || '—'),
        'Forme    : ' + (state.formeCode || '—'),
        'Variant  : ' + (state.variantId || '—'),
        'Coloris  : ' + (state.colorisCode || '—'),
        'Tranche  : ' + (state.trancheDecorCode || '—'),
        'Option   : ' + (state.optionRequired ? (state.optionCode || '⚠️ requis') : 'n/a'),
        'ATC      : ' + (result.valid ? '✅ prêt' : '❌ ' + result.reasons.join(', ')),
      ].join('<br>');
    },
  };

  function createState(productId) {
    const KEY = SC_CONFIG.storagePrefix + productId;
    const s = {
      variantId: null, gammeCode: null, gammeNameVal: null,
      finitionCode: null, finitionName: null,
      formeCode: null, formeName: null,
      colorisCode: null, colorisName: null, colorisHex: null,
      optionCode: null, optionName: null, optionAtelierCode: null, optionRequired: false,
      trancheDecorCode: null, trancheDecorName: null,
      sansCouleur: false,
    };
    function save()    { try { sessionStorage.setItem(KEY, JSON.stringify(s)); } catch(e){} }
    function restore() { try { const d = JSON.parse(sessionStorage.getItem(KEY)||'null'); if(d) Object.assign(s,d); } catch(e){} }
    function clear()   { try { sessionStorage.removeItem(KEY); } catch(e){} Object.assign(s, { variantId:null, gammeCode:null, gammeNameVal:null, finitionCode:null, finitionName:null, formeCode:null, formeName:null, colorisCode:null, colorisName:null, colorisHex:null, optionCode:null, optionName:null, optionAtelierCode:null, optionRequired:false, trancheDecorCode:null, trancheDecorName:null, sansCouleur:false }); }
    return { s, save, restore, clear };
  }

  function validate(state) {
    const failed = SC_CONFIG.requiredFields.filter(f => !f.validate(state));
    const reasons = failed.map(f => f.label + ' manquant');
    const message = failed.length > 0 && failed[0].message ? failed[0].message(state) : 'Merci de compléter votre sélection.';
    return { valid: failed.length === 0, reasons, message };
  }

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney && window.scMoneyFormat) {
      return window.Shopify.formatMoney(cents, window.scMoneyFormat);
    }
    return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  }

  function readConfiguratorData(rootEl) {
    var dataEl = rootEl.querySelector('#sc-configurator-data');
    if (!dataEl) return null;
    try { return JSON.parse(dataEl.textContent); } catch (e) { return null; }
  }

  // ── Catalogue : socle boutique + surcouche produit ──────────────────────────
  // Le catalogue est stocké en deux metafields pour tenir sous la limite
  // Shopify de 128 Ko (cf. scripts/build-catalog-json.py) :
  //   - le socle (shop) porte les listes complètes, URLs d'images relatives ;
  //   - le produit porte ses variants, sa forme, ses filtres et ses descriptions.
  // On reconstitue ici l'objet complet attendu par le reste du moteur, à
  // l'identique de l'ancien format — rien d'autre ne change en aval.

  function expandCdnUrls(base) {
    var cdn = base.cdn || '';
    function abs(u) { return (!u || /^https?:\/\//.test(u)) ? u : cdn + u; }
    function absList(a) { return (a || []).map(abs); }
    (base.gammes || []).forEach(function(g) { g.imageUrl = abs(g.imageUrl); });
    (base.finitions || []).forEach(function(f) { f.imageUrl = abs(f.imageUrl); f.imageSituationUrls = absList(f.imageSituationUrls); });
    (base.coloris || []).forEach(function(c) { c.imageUrl = abs(c.imageUrl); c.imageSituationUrls = absList(c.imageSituationUrls); });
    (base.formes || []).forEach(function(f) { f.iconUrl = abs(f.iconUrl); });
    (base.optionsPoignee || []).forEach(function(o) { o.iconUrl = abs(o.iconUrl); });
    (base.tranchesDecor || []).forEach(function(t) { t.iconUrl = abs(t.iconUrl); });
    return base;
  }

  function assembleCatalog(base, prod) {
    if (!prod) return null;
    // Rétrocompatibilité : tant que le catalogue n'a pas été regénéré au format
    // v3, le metafield produit porte encore les listes complètes. On le prend
    // alors tel quel. Permet de déployer le thème et de regénérer les données
    // dans n'importe quel ordre, et de revenir en arrière sans casse.
    if (!base || (prod.coloris && prod.coloris.length)) {
      log('catalogue au format v2 (listes dans le metafield produit)');
      return prod;
    }
    var b = expandCdnUrls(base);
    var exclus     = new Set(prod.colorisExclus || []);
    var gammeOk    = new Set(prod.gammeCodes || []);
    var finitionOk = new Set(prod.finitionCodes || []);
    var descriptions = prod.finitionDescriptions || {};
    // Paragraphe d'introduction facultatif, affiché avant la description
    // (champ « 1er bloc description » du metaobject Finition).
    var intros = prod.finitionIntros || {};
    return {
      version: prod.version,
      selfFormeCode: prod.selfFormeCode,
      variants: prod.variants || [],
      formes: b.formes || [],
      optionsPoignee: b.optionsPoignee || [],
      tranchesDecor: b.tranchesDecor || [],
      gammes: (b.gammes || []).filter(function(g) { return gammeOk.has(g.code); }),
      coloris: (b.coloris || []).filter(function(c) { return !exclus.has(c.code); }),
      finitions: (b.finitions || [])
        .filter(function(f) { return finitionOk.has(f.code); })
        .map(function(f) {
          var copy = Object.assign({}, f);
          copy.description = descriptions[f.code] || '';
          copy.descriptionIntro = intros[f.code] || '';
          delete copy.descriptionRaw;
          delete copy.introRaw;
          return copy;
        }),
    };
  }

  // Instance courante — réassignée à chaque (ré)initialisation, y compris après un swap AJAX.
  // Permet aux listeners globaux (variant:changed) de ne jamais être ré-attachés en double.
  let current = null;

  function initConfigurator(rootEl, opts) {
    opts = opts || {};
    const data = readConfiguratorData(rootEl);
    if (!data) { log('données manquantes'); return; }

    window.scMoneyFormat = data.moneyFormat;

    const productId = data.productId;
    const productData = { id: productId, variants: data.variants, selectedVariantId: data.selectedVariantId };
    const catalogData = assembleCatalog(data.catalogBase, data.catalog);
    if (!catalogData || !(catalogData.coloris || []).length) {
      log('catalogue manquant', { socle: !!data.catalogBase, produit: !!data.catalog });
      console.warn('[SC] Catalogue illisible — relancer scripts/build-catalog-json.py');
      return;
    }

    // ── Libellé d'affichage des finitions ────────────────────────────────────
    // Les metaobjects sont dupliqués par gamme, d'où des noms comme "Lisse
    // couleur", "Lisse décor bois", "Lisse bois naturel" qui désignent la même
    // finition. Côté client on n'affiche que "Lisse" : le suffixe reprend le nom
    // de la gamme, déjà indiquée juste au-dessus dans le configurateur.
    // Le nom complet reste la valeur technique — c'est lui qui correspond aux
    // options de variant Shopify, on ne le touche pas.
    function libelleFinition(nomComplet, gammeCode) {
      var nom = (nomComplet || '').trim();
      var gamme = (catalogData.gammes || []).find(function(g) { return g.code === gammeCode; });
      if (!gamme || !gamme.name) return nom;
      var suffixe = ' ' + gamme.name.trim().toLowerCase();
      if (nom.toLowerCase().slice(-suffixe.length) === suffixe) {
        return nom.slice(0, nom.length - suffixe.length).trim() || nom;
      }
      return nom;
    }

    var finitionCodeToLabel = {};
    (catalogData.finitions || []).forEach(function(f) {
      finitionCodeToLabel[f.code] = (f.name || '').toLowerCase();
    });

    SCDebug.init(productId);

    const { s: state, save, restore } = createState(productId);
    state.variantId = productData.selectedVariantId;

    var previewBaseUrl = null;

    const atcBtn         = rootEl.querySelector('#sc-atc-btn');
    const errorEl        = rootEl.querySelector('#sc-coloris-error');
    const gammeBtns      = rootEl.querySelectorAll('[data-sc-gamme]');
    const finitionBtns   = rootEl.querySelectorAll('[data-sc-finition]');
    const formeLinks     = rootEl.querySelectorAll('[data-sc-forme]');
    const swatches       = rootEl.querySelectorAll('[data-sc-coloris]');
    const optionBtns     = rootEl.querySelectorAll('[data-sc-option]');
    const trancheBtns    = rootEl.querySelectorAll('[data-sc-tranche]');
    const stepTranche    = rootEl.querySelector('#sc-step-tranche');
    const stepColoris    = rootEl.querySelector('#sc-step-coloris');
    // Fiche Échantillons : voir `ui` dans UNIVERS (scripts/build-catalog-json.py).
    const isEchantillons = !!(data.catalog && data.catalog.ui && data.catalog.ui.mode === 'echantillons');
    const formatBtns     = rootEl.querySelectorAll('[data-sc-format]');
    const formatSelect   = rootEl.querySelector('[data-sc-format-select]');
    // Les vignettes de situation sont construites en JS à la demande : la liste
    // doit être relue à chaque appel, pas figée à l'initialisation.
    const thumbsContainer = rootEl.querySelector('#sc-gallery-thumbs');
    function galleryThumbItems() {
      return [].slice.call(rootEl.querySelectorAll('#sc-gallery-thumbs [data-sc-gallery-index]'));
    }
    const gallerySlideItems = rootEl.querySelectorAll('#sc-gallery-main [data-sc-gallery-index]');
    const zoomBtn           = rootEl.querySelector('.product__zoom-button');
    const finitionSelect    = rootEl.querySelector('#sc-finition-select');
    const descriptionEl  = rootEl.querySelector('#sc-description-dynamic');
    const stepOption     = rootEl.querySelector('#sc-step-option');
    var defaultDescriptionHtml = descriptionEl ? descriptionEl.innerHTML : '';

    if (!atcBtn) { log('bouton ATC introuvable'); return; }

    // Applique le libellé court aux boutons de finition rendus par Liquid :
    // infobulle, texte éventuel, et mémorisation pour l'étape et le panier.
    finitionBtns.forEach(function(b) {
      var court = libelleFinition(b.dataset.scFinitionName, b.dataset.scFinitionGamme);
      b.dataset.scFinitionLabel = court;
      b.setAttribute('aria-label', court);
      var span = b.querySelector('.sc-finition-btn__name');
      if (span) span.textContent = court;
    });

    function showStep(el) { if (el) el.classList.remove('sc-step--hidden'); }
    function hideStep(el) { if (el) el.classList.add('sc-step--hidden'); }

    function refreshUI() {
      const result = validate(state);
      atcBtn.classList.toggle('sc-atc-btn--locked', !result.valid);
      // Le message sous le bouton est permanent tant que la sélection est
      // incomplète (et non plus affiché seulement au clic) : il indique en
      // continu ce qu'il reste à choisir.
      if (result.valid) { hideError(); } else { showError(result.message); }
      refreshTailleHint();
      refreshSampleLink();
      // Le SKU atelier n'était mis à jour qu'au choix d'une position ou à un
      // changement de variant : un client qui ne touchait pas à la taille
      // commandait sans référence atelier. On le recalcule après chaque
      // sélection, refreshUI() étant appelé par toutes.
      updateSkuAtelier();
      SCDebug.update(state, result);
    }

    // Lien « Commander un échantillon » : emmène sur la fiche Échantillons avec
    // la collection et la finition déjà choisies, sans coloris. Un code inconnu
    // de cette fiche (Bulle, Encoche… ) y est ignoré : elle retombe alors sur la
    // première finition de la collection.
    function refreshSampleLink() {
      var link = rootEl.querySelector('#sc-sample-link');
      if (!link) return;
      var url = link.dataset.scSampleUrl || link.getAttribute('href');
      var params = [];
      if (state.gammeCode)    params.push('sc_gamme=' + encodeURIComponent(state.gammeCode));
      if (state.finitionCode) params.push('sc_finition=' + encodeURIComponent(state.finitionCode));
      link.setAttribute('href', params.length ? url + '?' + params.join('&') : url);
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || 'Merci de compléter votre sélection.';
      errorEl.hidden = false;
    }
    function hideError() {
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
    }

    // Accents et casse retirés pour comparer des libellés saisis à la main.
    function normalizeLabel(s) {
      return (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    }

    function refreshTailleHint() {
      var hintEl = rootEl.querySelector('#sc-taille-hint');
      if (!hintEl) return;
      var cfg = SC_CONFIG.tailleHint;
      var label = normalizeLabel(finitionCodeToLabel[state.finitionCode] || state.finitionName);
      var concerned = state.formeCode === cfg.formeCode
        && !!label
        && cfg.finitionLabels.some(function(f) { return label.indexOf(f) === 0; });
      hintEl.textContent = concerned ? cfg.text : '';
      hintEl.hidden = !concerned;
    }

    function setPreviewImage(url) {
      var img     = rootEl.querySelector('#sc-preview-img');
      var ph      = rootEl.querySelector('#sc-preview-placeholder');
      var preview = rootEl.querySelector('#sc-color-preview');
      var wash    = rootEl.querySelector('#sc-preview-color-wash');
      if (url) {
        if (img) {
          img.style.opacity = '0';
          img.src = url;
          img.alt = '';
          requestAnimationFrame(function() {
            requestAnimationFrame(function() { img.style.opacity = '1'; });
          });
        }
        if (ph) ph.style.opacity = '0';
        if (preview) preview.style.backgroundColor = '#f2ede8';
        if (wash) wash.style.opacity = '0';
      } else {
        if (img) { img.style.opacity = '0'; img.src = ''; }
        if (ph) ph.style.opacity = '1';
        if (wash) wash.style.opacity = '0';
      }
    }

    function updatePreview(hex, name, imageUrl) {
      var preview     = rootEl.querySelector('#sc-color-preview');
      var img         = rootEl.querySelector('#sc-preview-img');
      var placeholder = rootEl.querySelector('#sc-preview-placeholder');
      var badge       = rootEl.querySelector('#sc-preview-badge');
      var badgeDot    = rootEl.querySelector('#sc-preview-badge-dot');
      var badgeName   = rootEl.querySelector('#sc-preview-badge-name');
      var wash        = rootEl.querySelector('#sc-preview-color-wash');
      if (!preview) return;

      if (imageUrl) {
        if (img) {
          img.style.opacity = '0';
          img.src = imageUrl;
          img.alt = name;
          requestAnimationFrame(function() {
            requestAnimationFrame(function() { img.style.opacity = '1'; });
          });
        }
        if (placeholder) placeholder.style.opacity = '0';
        if (wash) wash.style.opacity = '0';
        preview.style.backgroundColor = '#f2ede8';
      } else if (previewBaseUrl) {
        if (wash) {
          wash.style.backgroundColor = hex;
          wash.style.opacity = '0.2';
        }
        preview.style.backgroundColor = '#f2ede8';
        if (img && img.src !== previewBaseUrl) { img.src = previewBaseUrl; }
        if (img) img.style.opacity = '1';
        if (placeholder) placeholder.style.opacity = '0';
      } else {
        if (img) { img.style.opacity = '0'; img.src = ''; }
        if (placeholder) placeholder.style.opacity = '1';
        if (wash) wash.style.opacity = '0';
        preview.style.backgroundColor = hex;
      }

      if (badge) {
        badge.style.display = 'flex';
        if (badgeDot) badgeDot.style.backgroundColor = hex;
        if (badgeName) badgeName.textContent = name;
      }
    }

    // Diapositive unique et réutilisée pour tous les visuels de situation.
    // Le serveur n'en rend plus qu'une : on l'alimente depuis la vignette
    // sélectionnée, en repassant sa source de la largeur vignette à 900px.
    var situationSlide = rootEl.querySelector('#sc-gallery-situation');
    var situationImg   = situationSlide ? situationSlide.querySelector('img') : null;

    function fillSituationSlide(thumb) {
      if (!situationSlide || !situationImg) return;
      var timg = thumb.querySelector('img');
      if (!timg) return;
      var src = (timg.getAttribute('src') || '').replace(/([?&])width=\d+/, '$1width=900');
      // Cette image est le LCP : ni chargement différé, ni priorité par défaut.
      situationImg.loading = 'eager';
      situationImg.setAttribute('fetchpriority', 'high');
      if (situationImg.getAttribute('src') !== src) situationImg.setAttribute('src', src);
      situationImg.alt = timg.getAttribute('alt') || '';
      situationSlide.dataset.scGalleryIndex = thumb.dataset.scGalleryIndex;
      // Le bouton zoom ne s'active que sur les visuels de coloris : on ne
      // reporte cet attribut que lorsqu'il est présent sur la vignette.
      if (thumb.dataset.scGalleryColoris) situationSlide.dataset.scGalleryColoris = thumb.dataset.scGalleryColoris;
      else delete situationSlide.dataset.scGalleryColoris;
    }

    // Ramène la vignette choisie au centre de la bande, comme la fiche produit
    // historique du site (demande Agnès 21/09). Le calcul est borné à zéro :
    // tant que la vignette n'a pas dépassé le milieu, rien ne bouge — c'est
    // exactement le comportement décrit. La bande est verticale en desktop et
    // horizontale en mobile, d'où les deux axes.
    function centrerVignetteActive(vignette) {
      var piste = thumbsContainer;
      if (!piste || !vignette || !piste.scrollTo) return;
      var repere = piste.getBoundingClientRect();
      var cadre = vignette.getBoundingClientRect();
      if (piste.scrollHeight > piste.clientHeight + 1) {
        var haut = piste.scrollTop + (cadre.top - repere.top) - (piste.clientHeight - cadre.height) / 2;
        piste.scrollTo({ top: Math.max(0, haut), behavior: 'smooth' });
      } else if (piste.scrollWidth > piste.clientWidth + 1) {
        var gauche = piste.scrollLeft + (cadre.left - repere.left) - (piste.clientWidth - cadre.width) / 2;
        piste.scrollTo({ left: Math.max(0, gauche), behavior: 'smooth' });
      }
    }

    function setGalleryActive(index) {
      galleryThumbItems().forEach(function(t) { t.classList.toggle('sc-gallery__thumb--active', t.dataset.scGalleryIndex === index); });
      var thumb = rootEl.querySelector('#sc-gallery-thumbs [data-sc-gallery-index="' + index + '"]');
      if (thumb) centrerVignetteActive(thumb);
      var estSituation = !!(thumb && (thumb.dataset.scGalleryColoris || thumb.dataset.scGalleryFinition));
      var activeSlide = null;

      if (estSituation) {
        fillSituationSlide(thumb);
        gallerySlideItems.forEach(function(s) {
          s.classList.remove('sc-gallery__slide--active');
          if (s.classList.contains('product__media-item')) s.classList.remove('is-selected');
        });
        situationSlide.classList.add('sc-gallery__slide--active');
        // is-selected : c'est par cette classe que Focal repère la vignette
        // d'origine du zoom (getThumbBoundsFn).
        situationSlide.classList.add('is-selected');
        activeSlide = situationSlide;
      } else {
        if (situationSlide) situationSlide.classList.remove('sc-gallery__slide--active', 'is-selected');
        gallerySlideItems.forEach(function(s) {
          var isActive = s.dataset.scGalleryIndex === index;
          s.classList.toggle('sc-gallery__slide--active', isActive);
          if (s.classList.contains('product__media-item')) s.classList.toggle('is-selected', isActive);
          if (isActive) activeSlide = s;
        });
      }

      if (zoomBtn) {
        // Tous les visuels de situation sont zoomables, pas seulement ceux des
        // coloris : le zoom passe désormais par PhotoSwipe pour l'ensemble, la
        // restriction d'origine n'a plus lieu d'être. Elle laissait les visuels
        // de finition — ceux affichés avant tout choix de coloris — sans icône.
        var estSituation = !!activeSlide && activeSlide === situationSlide;
        var estMediaProduit = !!activeSlide && !estSituation && activeSlide.getAttribute('data-media-type') === 'image';
        zoomBtn.hidden = !estMediaProduit && !estSituation;
      }
    }

    // ── Zoom des visuels de situation ────────────────────────────────────────
    // Les visuels de situation viennent des metaobjects, pas de product.media :
    // _buildItems() de Focal, qui rapproche chaque image du JSON produit pour en
    // lire les dimensions, ne peut donc pas les inclure. Plutôt qu'une lightbox
    // maison au comportement différent, on surcharge _buildItems SUR L'INSTANCE
    // pour lui fournir l'item manquant. Le bouton natif ouvre alors le même
    // PhotoSwipe que les visuels produit — même habillage, mêmes gestes.

    var largeurZoom = 1800;

    // Dimensions réelles de l'image de zoom. En repli, on garde le ratio de la
    // vignette déjà chargée plutôt qu'un ratio arbitraire, pour que PhotoSwipe
    // cadre correctement même si la sonde échoue.
    function mesurerImage(src, ratioReplis) {
      var ratio = ratioReplis || 0.75;
      return new Promise(function(resolve) {
        var sonde = new Image();
        sonde.onload  = function() { resolve({ w: sonde.naturalWidth || largeurZoom, h: sonde.naturalHeight || Math.round(largeurZoom * ratio) }); };
        sonde.onerror = function() { resolve({ w: largeurZoom, h: Math.round(largeurZoom * ratio) }); };
        sonde.src = src;
      });
    }

    // Les vignettes de situation actuellement visibles forment la série
    // parcourable dans le zoom — celles du coloris choisi, ou à défaut celles
    // de la finition. C'est ce que l'utilisateur voit dans la colonne.
    function vignettesSituationVisibles() {
      return [].slice.call(rootEl.querySelectorAll('#sc-gallery-thumbs [data-sc-situation-thumb]'));
    }

    var zoomEl = rootEl.querySelector('product-image-zoom');

    // La modale vit hors de .sc-media-wrapper pour échapper au contexte
    // d'empilement créé par son position:sticky (cf. le commentaire dans la
    // section). Focal résout mediaElement via closest('.product__media') au
    // moment de connectedCallback : hors de la galerie il trouve null, ce qui
    // ferait planter getThumbBoundsFn. On le rétablit ici.
    if (zoomEl && !zoomEl.mediaElement) {
      zoomEl.mediaElement = rootEl.querySelector('#sc-gallery-main');
    }

    if (zoomEl && typeof zoomEl._buildItems === 'function') {
      var buildItemsNatif = zoomEl._buildItems.bind(zoomEl);
      zoomEl._buildItems = function() {
        var actif = rootEl.querySelector('#sc-gallery-main .sc-gallery__slide--active');
        if (!actif || actif !== situationSlide) return buildItemsNatif();
        var vignettes = vignettesSituationVisibles();
        if (!vignettes.length) return buildItemsNatif();

        // Toute la série est renvoyée, pas seulement le visuel affiché : c'est
        // ce qui donne à PhotoSwipe ses flèches gauche/droite, comme sur les
        // visuels produit. L'index d'ouverture vient du drapeau `selected`.
        var indexActif = situationSlide.dataset.scGalleryIndex;
        return Promise.all(vignettes.map(function(t) {
          var timg = t.querySelector('img');
          var src = (timg && timg.getAttribute('src')) || '';
          // Définition supérieure à celle affichée pour le zoom.
          var srcZoom = src.replace(/([?&])width=\d+/, '$1width=' + largeurZoom);
          var ratio = timg && timg.naturalWidth ? timg.naturalHeight / timg.naturalWidth : 0.75;
          return mesurerImage(srcZoom, ratio).then(function(d) {
            return {
              src: srcZoom, msrc: src, w: d.w, h: d.h,
              selected: t.dataset.scGalleryIndex === indexActif,
            };
          });
        }));
      };
    } else if (zoomEl) {
      log('zoom : _buildItems introuvable sur product-image-zoom — visuels de situation non zoomables');
    }

    var emptySlide = rootEl.querySelector('#sc-gallery-main .sc-gallery__empty');

    // ── Vignettes de situation, construites à la demande ─────────────────────
    // Le serveur ne rend plus que les vignettes de product.media. Les visuels de
    // situation — près de 500 sur ce produit, pour 363 Ko de HTML — sont
    // construits ici à partir du catalogue déjà présent dans l'îlot JSON, et
    // seulement ceux de la sélection courante : quelques-uns au lieu de tous.

    var largeurVignette = 130;

    function urlAvecLargeur(url, largeur) {
      if (!url) return '';
      return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + largeur;
    }

    function viderVignettesSituation() {
      [].slice.call(rootEl.querySelectorAll('#sc-gallery-thumbs [data-sc-situation-thumb]'))
        .forEach(function(el) { el.remove(); });
    }

    function afficherVignettesProduit(visible) {
      galleryThumbItems().forEach(function(t) {
        if (!t.dataset.scSituationThumb) t.style.display = visible ? '' : 'none';
      });
    }

    // sources : [{ url, nom }] — meta porte le code coloris ou finition d'origine,
    // que setGalleryActive et le zoom relisent sur la vignette.
    function construireVignettesSituation(sources, meta) {
      viderVignettesSituation();
      if (!thumbsContainer) return 0;
      sources.forEach(function(s, i) {
        if (!s.url) return;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sc-gallery__thumb';
        b.dataset.scGalleryIndex = 's' + i;
        b.dataset.scSituationThumb = '1';
        if (meta.colorisCode) b.dataset.scGalleryColoris = meta.colorisCode;
        if (meta.finitionCode) b.dataset.scGalleryFinition = meta.finitionCode;
        b.setAttribute('aria-label', s.nom || '');
        var img = document.createElement('img');
        img.src = urlAvecLargeur(s.url, largeurVignette);
        img.alt = s.nom || '';
        img.width = 100; img.height = 100;
        img.loading = 'lazy';
        b.appendChild(img);
        b.addEventListener('click', function() { setGalleryActive(b.dataset.scGalleryIndex); });
        thumbsContainer.appendChild(b);
      });
      return thumbsContainer.querySelectorAll('[data-sc-situation-thumb]').length;
    }

    function visuelsDeColoris(c) {
      if (!c) return [];
      var urls = (c.imageSituationUrls && c.imageSituationUrls.length) ? c.imageSituationUrls
               : (c.imageUrl ? [c.imageUrl] : []);
      return urls.map(function(u) { return { url: u, nom: c.name }; });
    }

    function visuelsDeFinition(f) {
      if (!f || !f.imageSituationUrls) return [];
      return f.imageSituationUrls.map(function(u) { return { url: u, nom: f.name }; });
    }

    // Visuels génériques produit (product.media) — état par défaut, avant ou hors sélection Gamme+Finition.
    function showGenericGallery() {
      if (emptySlide) emptySlide.classList.add('sc-gallery__slide--active');
      viderVignettesSituation();
      afficherVignettesProduit(true);
      var firstThumb = rootEl.querySelector('#sc-gallery-thumbs [data-sc-gallery-index]:not([data-sc-situation-thumb])');
      if (firstThumb) setGalleryActive(firstThumb.dataset.scGalleryIndex);
    }

    // Visuels des coloris disponibles pour la Gamme+Finition choisie. Retombe sur la galerie
    // générique (showGenericGallery) si aucun coloris de cette combinaison n'a d'image.
    function showColorisGallery(gammeCode, finitionCode) {
      var c = (catalogData.coloris || []).find(function(x) {
        return x.gammeCode === gammeCode && x.finitionCode === finitionCode && visuelsDeColoris(x).length;
      });
      if (!c) return false;
      return showColorisGalleryForCode(c.code);
    }

    // Bascule la galerie sur les visuels du coloris précisément cliqué.
    function showColorisGalleryForCode(colorisCode) {
      var c = (catalogData.coloris || []).find(function(x) { return x.code === colorisCode; });
      var sources = visuelsDeColoris(c);
      if (!sources.length) return false;
      if (emptySlide) emptySlide.classList.remove('sc-gallery__slide--active');
      afficherVignettesProduit(false);
      construireVignettesSituation(sources, { colorisCode: colorisCode });
      setGalleryActive('s0');
      return true;
    }

    // Visuels par défaut de la Finition choisie, affichés avant tout choix de Coloris.
    // Retombe sur la galerie générique (showGenericGallery) si la finition n'a pas d'image_situation.
    function showFinitionGallery(finitionCode) {
      var f = (catalogData.finitions || []).find(function(x) { return x.code === finitionCode; });
      var sources = visuelsDeFinition(f);
      if (!sources.length) return false;
      if (emptySlide) emptySlide.classList.remove('sc-gallery__slide--active');
      afficherVignettesProduit(false);
      construireVignettesSituation(sources, { finitionCode: finitionCode });
      setGalleryActive('s0');
      return true;
    }

    function updateVariantForFinition(finitionName, tailleValOverride) {
      if (!productData.variants) return;
      var tailleVal = tailleValOverride;
      if (!tailleVal) {
        const activeVar = productData.variants.find(function(v) { return v.id == state.variantId; });
        if (!activeVar) return;
        tailleVal = activeVar.option1;
      }
      var finLower = finitionName ? finitionName.toLowerCase() : '';
      var target = productData.variants.find(function(v) {
        return v.option1 === tailleVal && (v.option2 || '').toLowerCase() === finLower;
      });
      if (target) {
        state.variantId = target.id;
        var form = atcBtn && atcBtn.closest('form');
        var idInput = form && form.querySelector('.product-variant-id');
        if (idInput) { idInput.disabled = false; idInput.value = String(target.id); }
        document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant: target }, bubbles: true }));
        log('variant →', target.title);
        refreshFormatUI();
      }
    }

    // ── Fiche Échantillons ───────────────────────────────────────────────────
    // Ce que le variant actif commande à l'interface : bouton de format actif,
    // étapes Couleur/Tranche (masquées pour un pack), ligne « Produit : … ».
    // Idempotent, sans effet hors de l'univers Échantillons.
    function catalogVariantActif() {
      return (catalogData.variants || []).find(function(cv) { return cv.id == state.variantId; }) || null;
    }

    // Un pack ne porte ni couleur ni tranche : on vide les propriétés du panier
    // le temps qu'il est sélectionné, et on les rétablit si le client revient
    // sur un format qui les demande — sa sélection, elle, n'est jamais perdue.
    function syncProprietesCouleur() {
      function set(id, v) { var el = rootEl.querySelector('#' + id); if (el) el.value = v || ''; }
      var vide = state.sansCouleur;
      set('sc-prop-coloris', vide ? '' : state.colorisName);
      set('sc-prop-tranche', vide ? '' : state.trancheDecorName);
    }

    function refreshFormatUI() {
      if (!isEchantillons) return;
      var shopVar = productData.variants.find(function(v) { return v.id == state.variantId; });
      var format = shopVar && shopVar.option1;
      // Un format sans variant pour la finition en cours n'est pas proposé : « 2
      // échantillons » n'existe qu'en Tranche décor bois, finition que la fiche ne
      // montre plus (débrief Agnès 16/10) ; son bouton ne ferait rien.
      var finEnCours = (state.finitionName || '').toLowerCase();
      formatBtns.forEach(function(b) {
        var existe = productData.variants.some(function(v) {
          return v.option1 === b.dataset.scFormat && (v.option2 || '').toLowerCase() === finEnCours;
        });
        b.hidden = !existe && !!finEnCours;
        var on = b.dataset.scFormat === format;
        b.classList.toggle('sc-format-btn--active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      if (formatSelect && format) formatSelect.value = format;
      setStepValue('sc-step-taille-value', format || '');

      // Les champs du variant (ligne « Produit », sans couleur, taille) viennent d'abord
      // de l'îlot de la page, lu en direct côté Liquid : un changement dans l'admin
      // se voit au rechargement, sans regénérer le catalogue. Le catalogue ne sert
      // plus que de repli si l'îlot ne les porte pas.
      var cvCatalogue = catalogVariantActif();
      var live = shopVar && shopVar.sc;
      var cv = live
        ? { sansCouleur: live.sansCouleur === true, produitLigne: live.ligne || '', taille: live.taille || '' }
        : cvCatalogue;
      state.sansCouleur = !!(cv && cv.sansCouleur);
      if (state.sansCouleur) { hideStep(stepColoris); } else { showStep(stepColoris); }
      if (state.sansCouleur || !besoinTranche(state)) { hideStep(stepTranche); } else { showStep(stepTranche); }
      syncProprietesCouleur();

      var tailleEl = rootEl.querySelector('#sc-recap-taille-value');
      if (tailleEl) tailleEl.textContent = (cv && cv.taille) || tailleEl.dataset.defaut || tailleEl.textContent;

      var ligne = (cv && cv.produitLigne) || '';
      var ligneEl = rootEl.querySelector('#sc-recap-produit');
      var valeurEl = rootEl.querySelector('#sc-recap-produit-value');
      if (ligneEl) ligneEl.hidden = !ligne;
      if (valeurEl) valeurEl.textContent = ligne;
      var pProduit = rootEl.querySelector('#sc-prop-produit');
      if (pProduit) pProduit.value = ligne;

      refreshUI();
    }

    function setStepValue(id, text, dotHex) {
      var el = rootEl.querySelector('#' + id);
      if (el) el.textContent = text || '';
      if (id === 'sc-step-coloris-value') {
        var colorisStep = rootEl.querySelector('#sc-step-coloris');
        if (colorisStep) colorisStep.classList.toggle('sc-step--has-coloris', !!text);
        var dot = rootEl.querySelector('#sc-step-coloris-dot');
        if (dot && dotHex) dot.style.backgroundColor = dotHex;
      }
    }

    // La forme est désormais fixe pour ce produit (1 produit = 1 forme) : on l'affiche,
    // on ne la sélectionne plus localement. Changer de forme = swapToProduct().
    function markCurrentForme() {
      var selfCode = catalogData.selfFormeCode;
      var formeMeta = (catalogData.formes || []).find(function(f) { return f.code === selfCode; });
      state.formeCode = selfCode || null;
      state.formeName = formeMeta ? formeMeta.name : null;
      setStepValue('sc-step-forme-value', state.formeName);
      formeLinks.forEach(function(a) {
        var active = a.dataset.scForme === selfCode;
        a.classList.toggle('sc-forme-btn--active', active);
        a.setAttribute('aria-checked', active ? 'true' : 'false');
      });
    }

    function resetOption() {
      state.optionCode = null; state.optionName = null; state.optionAtelierCode = null; state.optionRequired = false;
      setStepValue('sc-step-option-value', '');
      optionBtns.forEach(function(b) { b.classList.remove('sc-option-btn--active'); b.setAttribute('aria-checked', 'false'); });
      var pOpt = rootEl.querySelector('#sc-prop-option');
      if (pOpt) pOpt.value = '';
      // Recalcule au lieu de vider : sans position de poignée, le SKU atelier
      // reste celui du variant, simplement sans suffixe. Le vider laissait
      // partir la commande sans référence pour l'atelier.
      updateSkuAtelier();
      hideStep(stepOption);
    }

    function updateSkuAtelier() {
      var pSku = rootEl.querySelector('#sc-prop-sku-atelier');
      if (!pSku) return;
      var variantId = state.variantId || productData.selectedVariantId;
      var v = productData.variants && productData.variants.find(function(v) { return v.id == variantId; });
      var sku = v ? (v.sku || '') : '';
      pSku.value = (sku && state.optionAtelierCode) ? sku + '_' + state.optionAtelierCode : sku;
    }

    function getAvailableOptions() {
      if (!state.finitionCode) return [];
      // Résoudre la taille courante depuis le variant Shopify actif
      var variantId = state.variantId || productData.selectedVariantId;
      var shopVar = productData.variants && productData.variants.find(function(v) {
        return v.id == variantId;
      });
      var tailleLabel = shopVar && shopVar.option1;
      // Chercher dans le catalogue par taille + finition (robuste vs. comparaison d'ID).
      // Comparer au LIBELLÉ (finitionCodeToLabel), pas au code slugifié : un code multi-mots
      // comme "prise-de-main" (tiret) ne matche jamais le libellé Shopify "Prise de main" (espace).
      var finLabel = finitionCodeToLabel[state.finitionCode] || state.finitionCode;
      var catVariant = null;
      if (tailleLabel) {
        catVariant = catalogData.variants && catalogData.variants.find(function(cv) {
          return cv.options &&
            cv.options.Taille === tailleLabel &&
            (cv.options.Finition || '').toLowerCase() === finLabel.toLowerCase();
        });
      }
      var allowed = catVariant && catVariant.positionsPoignee && catVariant.positionsPoignee.length > 0
        ? catVariant.positionsPoignee : null;
      return Array.prototype.filter.call(optionBtns, function(b) {
        if (b.dataset.scOptionFinition !== state.finitionCode) return false;
        if (!allowed) return true;
        return allowed.indexOf(b.dataset.scOption) >= 0;
      });
    }

    function refreshOptionStep() {
      if (!state.finitionCode) return;
      var availableOpts = getAvailableOptions();
      state.optionRequired = availableOpts.length > 0;
      optionBtns.forEach(function(b) {
        b.style.display = availableOpts.indexOf(b) >= 0 ? '' : 'none';
      });
      if (availableOpts.length > 0) {
        var stillValid = state.optionCode && availableOpts.some(function(b) {
          return b.dataset.scOption === state.optionCode;
        });
        if (!stillValid) {
          resetOption();
          selectOption(availableOpts[0].dataset.scOption, availableOpts[0].dataset.scOptionName);
        }
        showStep(stepOption);
      }
    }

    function selectOption(code, name) {
      var btn = rootEl.querySelector('[data-sc-option="' + code + '"]');
      var atelierCode = btn ? (btn.dataset.scOptionAtelierCode || '') : '';
      // Le libellé vient du bouton du produit COURANT, pas de l'état restauré :
      // un même code de position peut porter un nom différent d'une forme à
      // l'autre, ce qui laissait le libellé du tiroir affiché après un passage
      // sur la porte (débrief Agnès 16/07).
      name = (btn && btn.dataset.scOptionName) || name || '';
      state.optionCode = code; state.optionName = name; state.optionAtelierCode = atelierCode;
      setStepValue('sc-step-option-value', name);
      optionBtns.forEach(function(b) {
        var active = b.dataset.scOption === code;
        b.classList.toggle('sc-option-btn--active', active);
        b.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      var pOpt = rootEl.querySelector('#sc-prop-option');
      if (pOpt) pOpt.value = name;
      updateSkuAtelier();
      hideError();
      refreshUI();
      save();
      log('option →', code, atelierCode || '(no atelierCode)');
    }

    function resetTranche() {
      state.trancheDecorCode = null; state.trancheDecorName = null;
      setStepValue('sc-step-tranche-value', '');
      trancheBtns.forEach(function(b) { b.classList.remove('sc-tranche-btn--active'); b.setAttribute('aria-checked', 'false'); });
      var pTr = rootEl.querySelector('#sc-prop-tranche');
      if (pTr) pTr.value = '';
      hideStep(stepTranche);
    }

    function selectTranche(code, name) {
      state.trancheDecorCode = code; state.trancheDecorName = name;
      setStepValue('sc-step-tranche-value', name);
      trancheBtns.forEach(function(b) {
        var active = b.dataset.scTranche === code;
        b.classList.toggle('sc-tranche-btn--active', active);
        b.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      var pTr = rootEl.querySelector('#sc-prop-tranche');
      if (pTr) pTr.value = name;
      hideError();
      refreshUI();
      save();
      log('tranche →', code);
    }

    function filterFinitionsByTaille() {
      // Échantillons : le format dépend de la finition, pas l'inverse. Filtrer
      // les finitions par format ferait disparaître Tranche décor bois dès
      // qu'on choisit l'échantillon à l'unité, sans moyen de la retrouver.
      if (isEchantillons) return;
      if (!productData.variants || !state.gammeCode) return;
      var activeVar = productData.variants.find(function(v) { return v.id == state.variantId; });
      if (!activeVar || !activeVar.option1) return;
      var tailleVal = activeVar.option1;
      var availableLabels = [];
      productData.variants.forEach(function(v) {
        if (v.option1 === tailleVal && v.option2) {
          var lbl = v.option2.trim().toLowerCase();
          if (availableLabels.indexOf(lbl) < 0) availableLabels.push(lbl);
        }
      });
      if (availableLabels.length === 0) return;
      finitionBtns.forEach(function(b) {
        if (b.dataset.scFinitionGamme !== state.gammeCode) return;
        var finName = finitionCodeToLabel[b.dataset.scFinition] || '';
        var ok = availableLabels.some(function(lbl) { return finName.startsWith(lbl); });
        if (!ok) b.style.display = 'none';
      });
      if (state.finitionCode) {
        var activeFinBtn = rootEl.querySelector('[data-sc-finition="' + state.finitionCode + '"][data-sc-finition-gamme="' + state.gammeCode + '"]');
        if (activeFinBtn && activeFinBtn.style.display === 'none') {
          state.finitionCode = null; state.finitionName = null;
          state.colorisCode = null; state.colorisName = null; state.colorisHex = null;
          setStepValue('sc-step-finition-value', '');
          setStepValue('sc-step-coloris-value', '');
          swatches.forEach(function(s) { s.style.display = 'none'; });
          resetOption();
          resetTranche();
        }
      }
    }

    function filterTailleByFinition() {
      if (!productData.variants || !state.finitionCode) return;
      var finLabel = finitionCodeToLabel[state.finitionCode] || '';
      var availableTailles = [];
      productData.variants.forEach(function(v) {
        if (!v.option1 || !v.option2) return;
        var lbl = v.option2.trim().toLowerCase();
        if (finLabel.startsWith(lbl) || lbl.startsWith(finLabel)) {
          if (availableTailles.indexOf(v.option1) < 0) availableTailles.push(v.option1);
        }
      });
      if (availableTailles.length === 0) return;

      rootEl.querySelectorAll('.combo-box__option-item').forEach(function(item) {
        var vis = availableTailles.indexOf(item.getAttribute('value')) >= 0;
        item.style.display = vis ? '' : 'none';
      });

      formatBtns.forEach(function(b) {
        b.style.display = availableTailles.indexOf(b.dataset.scFormat) >= 0 ? '' : 'none';
      });

      var tailleSelect = rootEl.querySelector('select[aria-label="Taille"]');
      if (tailleSelect) {
        Array.prototype.forEach.call(tailleSelect.options, function(opt) {
          opt.disabled = availableTailles.indexOf(opt.value) < 0;
        });
      }

      // Si la taille actuellement sélectionnée n'existe pas pour cette finition,
      // basculer automatiquement sur la première taille disponible.
      var activeVar = productData.variants.find(function(v) { return v.id == state.variantId; });
      var currentTaille = activeVar && activeVar.option1;
      if (currentTaille && availableTailles.indexOf(currentTaille) < 0) {
        updateVariantForFinition(state.finitionName, availableTailles[0]);
      }
    }

    function estPlanDeTravail(nomFinition) {
      return /^plan de travail/i.test(nomFinition || '');
    }

    // Fiche Échantillons : « Façade », « Plan de travail & crédence » ou les deux,
    // selon les finitions que la collection propose (débrief Agnès 16/10).
    function typeDeProduit(gammeCode) {
      var facade = false, plan = false;
      finitionBtns.forEach(function(b) {
        if (b.dataset.scFinitionGamme !== gammeCode) return;
        if (estPlanDeTravail(b.dataset.scFinitionName)) { plan = true; } else { facade = true; }
      });
      return [facade ? 'Façade' : '', plan ? 'Plan de travail & crédence' : ''].filter(Boolean).join(' | ');
    }

    function selectGamme(gammeCode, gammeName, doSave) {
      if (doSave === undefined) doSave = true;
      state.gammeCode = gammeCode; state.gammeNameVal = gammeName;
      setStepValue('sc-step-gamme-value', gammeName);

      gammeBtns.forEach(function(b) {
        var active = b.dataset.scGamme === gammeCode;
        b.classList.toggle('sc-gamme-btn--active', active);
        b.setAttribute('aria-checked', active ? 'true' : 'false');
      });

      var pG = rootEl.querySelector('#sc-prop-gamme');
      if (pG) pG.value = gammeName;

      var gammeBtn = rootEl.querySelector('[data-sc-gamme="' + gammeCode + '"]');
      var gammeImgUrl = (gammeBtn && gammeBtn.dataset.scGammeImage) || null;
      previewBaseUrl = gammeImgUrl;
      setPreviewImage(gammeImgUrl);
      if (!gammeImgUrl) {
        var previewEl = rootEl.querySelector('#sc-color-preview');
        if (previewEl) previewEl.style.backgroundColor = SC_CONFIG.gammeColors[gammeCode] || '#f3f0ed';
      }
      var badge = rootEl.querySelector('#sc-preview-badge');
      if (badge) badge.style.display = 'none';

      var visibleFins = [];
      finitionBtns.forEach(function(b) {
        var vis = b.dataset.scFinitionGamme === gammeCode;
        b.style.display = vis ? '' : 'none';
        if (vis) visibleFins.push(b);
      });
      filterFinitionsByTaille();
      visibleFins = visibleFins.filter(function(b) { return b.style.display !== 'none'; });

      showGenericGallery();


      if (visibleFins.length > 0) {
        var finParDefaut = visibleFins[0];
        // Échantillons : la finition n'est plus proposée, on part d'une finition de
        // façade — c'est elle qui porte les trois formats, packs compris.
        if (isEchantillons) {
          finParDefaut = visibleFins.find(function(b) { return !estPlanDeTravail(b.dataset.scFinitionName); }) || finParDefaut;
        }
        selectFinition(finParDefaut.dataset.scFinition, finParDefaut.dataset.scFinitionName, false);
      } else {
        state.finitionCode = null; state.finitionName = null;
        state.colorisCode = null; state.colorisName = null; state.colorisHex = null;
        setStepValue('sc-step-finition-value', '');
        setStepValue('sc-step-taille-value',   '');
        setStepValue('sc-step-coloris-value',  '');
        swatches.forEach(function(s) { s.style.display = 'none'; });
        resetOption();
      }

      refreshUI();
      if (doSave) save();
      log('gamme →', gammeCode);
    }

    function selectFinition(finitionCode, finitionName, doSave) {
      if (doSave === undefined) doSave = true;
      state.finitionCode = finitionCode; state.finitionName = finitionName;
      state.colorisCode = null; state.colorisName = null; state.colorisHex = null;
      // Affichage et panier reçoivent le libellé court ; finitionName garde le
      // nom complet, seul valable pour retrouver l'option de variant Shopify.
      var finitionLibelle = libelleFinition(finitionName, state.gammeCode);
      setStepValue('sc-step-finition-value', isEchantillons ? typeDeProduit(state.gammeCode) : finitionLibelle);
      setStepValue('sc-step-taille-value',   '');
      setStepValue('sc-step-coloris-value',  '');

      filterTailleByFinition();

      var finBtn = rootEl.querySelector('[data-sc-finition="' + finitionCode + '"][data-sc-finition-gamme="' + state.gammeCode + '"]');
      var finImgUrl = (finBtn && finBtn.dataset.scFinitionImage) || null;
      if (finImgUrl) {
        previewBaseUrl = finImgUrl;
        setPreviewImage(finImgUrl);
      }
      var wash = rootEl.querySelector('#sc-preview-color-wash');
      if (wash) wash.style.opacity = '0';
      var badge = rootEl.querySelector('#sc-preview-badge');
      if (badge) badge.style.display = 'none';

      finitionBtns.forEach(function(b) {
        var active = b.dataset.scFinition === finitionCode && b.dataset.scFinitionGamme === state.gammeCode;
        b.classList.toggle('sc-finition-btn--active', active);
        b.setAttribute('aria-checked', active ? 'true' : 'false');
      });

      // Échantillons : un seul texte, celui du produit, qui présente les trois
      // formats. Les descriptions de finition parlent de façades, pas d'échantillons.
      if (descriptionEl && !isEchantillons) {
        var finEntry = (catalogData.finitions || []).find(function(f) {
          return f.code === finitionCode && f.gammeCode === state.gammeCode;
        });
        // Le bloc d'introduction, quand il est rempli, précède la description —
        // que celle-ci vienne de la finition ou, à défaut, du produit. C'est la
        // règle demandée par Agnès : parler du matériau en amont, sans déplacer
        // le texte existant (débrief 21/09).
        var corps = (finEntry && finEntry.description) ? finEntry.description : defaultDescriptionHtml;
        var intro = (finEntry && finEntry.descriptionIntro) || '';
        descriptionEl.innerHTML = intro + corps;
      }

      if (finitionSelect) {
        for (var i = 0; i < finitionSelect.options.length; i++) {
          if (finitionSelect.options[i].value.toLowerCase() === finitionName.toLowerCase()) {
            finitionSelect.options[i].selected = true;
            finitionSelect.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }

      updateVariantForFinition(finitionName);

      var matchingSwatches = [];
      swatches.forEach(function(s) {
        var match = s.dataset.scColorisFinition === finitionCode;
        s.style.display = match ? '' : 'none';
        if (match) matchingSwatches.push(s);
      });

      if (!showFinitionGallery(finitionCode)) showGenericGallery();

      state.colorisCode = null; state.colorisName = null; state.colorisHex = null;
      setStepValue('sc-step-coloris-value', '');
      resetOption();
      resetTranche();

      var availableOpts = getAvailableOptions();
      state.optionRequired = availableOpts.length > 0;
      if (availableOpts.length > 0) {
        optionBtns.forEach(function(b) { b.style.display = availableOpts.indexOf(b) >= 0 ? '' : 'none'; });
        showStep(stepOption);
        selectOption(availableOpts[0].dataset.scOption, availableOpts[0].dataset.scOptionName);
      }

      if (besoinTranche(state) && !state.sansCouleur) {
        showStep(stepTranche);
      }
      refreshFormatUI();

      hideError();
      refreshUI();
      if (doSave) save();
      log('finition →', finitionCode, '| coloris:', matchingSwatches.length);
    }

    function selectColoris(swatch, doSave) {
      if (doSave === undefined) doSave = true;
      var code     = swatch.dataset.scColoris;
      var name     = swatch.dataset.scColorisName;
      var hex      = swatch.dataset.scColorisHex;
      var imageUrl = swatch.dataset.scColorisImage || '';

      state.colorisCode = code; state.colorisName = name; state.colorisHex = hex;

      var pC = rootEl.querySelector('#sc-prop-coloris');
      if (pC) pC.value = name;

      setStepValue('sc-step-coloris-value', name, hex);

      swatches.forEach(function(s) {
        s.classList.toggle('sc-swatch--selected', s === swatch);
        s.setAttribute('aria-checked', s === swatch ? 'true' : 'false');
      });

      updatePreview(hex, name, imageUrl);
      if (!showColorisGalleryForCode(code)) showGenericGallery();

      hideError();
      refreshUI();
      if (doSave) save();
      log('coloris →', code, hex);
    }

    function swapToProduct(targetHandle, targetUrl) {
      if (!targetHandle || targetHandle === data.productHandle) return;

      var carry = {
        gammeCode: state.gammeCode, gammeNameVal: state.gammeNameVal,
        finitionCode: state.finitionCode, finitionName: state.finitionName,
        colorisCode: state.colorisCode, colorisName: state.colorisName, colorisHex: state.colorisHex,
        optionCode: state.optionCode, optionName: state.optionName,
      };

      // L'ancien contenu reste visible pendant le fetch — pas d'opacity à 0, pas de blank.
      // Swap instantané quand le fetch est prêt. Pas d'animation pour éviter tout flash.
      rootEl.style.pointerEvents = 'none';

      fetch('/products/' + targetHandle + '?section_id=sc-main-product-configurator')
        .then(function(res) {
          if (!res.ok) throw new Error('fetch failed: ' + res.status);
          return res.text();
        })
        .then(function(html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newRoot = doc.getElementById('sc-configurator-root');
          if (!newRoot) throw new Error('section sc-configurator-root introuvable dans la réponse');
          rootEl.replaceWith(newRoot);
          initConfigurator(newRoot, { carryOver: carry });
        })
        .catch(function(err) {
          rootEl.style.pointerEvents = '';
          log('swap échoué, navigation classique →', targetUrl, err);
          if (targetUrl) window.location.href = targetUrl;
        });
    }

    // Events
    gammeBtns.forEach(function(btn) {
      btn.addEventListener('click', function() { selectGamme(btn.dataset.scGamme, btn.dataset.scGammeName); });
    });
    finitionBtns.forEach(function(btn) {
      btn.addEventListener('click', function() { selectFinition(btn.dataset.scFinition, btn.dataset.scFinitionName); });
    });
    formeLinks.forEach(function(a) {
      a.addEventListener('click', function(e) {
        e.preventDefault();
        swapToProduct(a.dataset.scFormeHandle, a.dataset.scFormeUrl);
      });
    });
    swatches.forEach(function(s) {
      s.addEventListener('click', function() { selectColoris(s); });
      s.addEventListener('keydown', function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectColoris(s); } });
    });
    optionBtns.forEach(function(btn) {
      btn.addEventListener('click', function() { selectOption(btn.dataset.scOption, btn.dataset.scOptionName); });
      btn.addEventListener('keydown', function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectOption(btn.dataset.scOption, btn.dataset.scOptionName); } });
    });
    trancheBtns.forEach(function(btn) {
      btn.addEventListener('click', function() { selectTranche(btn.dataset.scTranche, btn.dataset.scTrancheName); });
      btn.addEventListener('keydown', function(e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectTranche(btn.dataset.scTranche, btn.dataset.scTrancheName); } });
    });

    formatBtns.forEach(function(btn) {
      btn.addEventListener('click', function() {
        updateVariantForFinition(state.finitionName, btn.dataset.scFormat);
        save();
      });
    });

    // Click guard (capture phase — intercepte avant loader-button de Focal)
    atcBtn.addEventListener('click', function(e) {
      var result = validate(state);
      if (!result.valid) {
        e.preventDefault(); e.stopImmediatePropagation();
        showError(result.message);
        var needsTranche = besoinTranche(state);
        var target = !state.gammeCode ? '#sc-step-gamme'
                   : !state.finitionCode ? '#sc-step-finition'
                   : needsTranche && !state.trancheDecorCode ? '#sc-step-tranche'
                   : !state.colorisCode ? '[data-sc-swatches]'
                   : state.optionRequired && !state.optionCode ? '#sc-step-option'
                   : '[data-sc-swatches]';
        var el = rootEl.querySelector(target);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        log('ATC bloqué :', result.reasons);
        return;
      }
    }, true);

    var form = atcBtn.closest('form');
    if (form) {
      form.addEventListener('submit', function(e) {
        var result = validate(state);
        if (!result.valid) {
          e.preventDefault(); e.stopImmediatePropagation();
          return;
        }
        var idInput = form.querySelector('.product-variant-id');
        if (idInput && idInput.disabled) {
          idInput.disabled = false;
          idInput.value = String(state.variantId || productData.selectedVariantId);
        }
        // Une property vide (Tranche, Produit, Position…) est quand même enregistrée
        // par Shopify et s'affiche dans l'admin de la commande comme une ligne sans
        // valeur (débrief Agnès 16/10). Un input désactivé n'est pas envoyé ; on
        // réévalue à chaque envoi, la sélection ayant pu changer depuis le précédent.
        Array.prototype.forEach.call(form.querySelectorAll('input[name^="properties["]'), function(input) {
          input.disabled = input.value === '';
        });
        log('submit →', { variant: idInput && idInput.value, coloris: state.colorisName });
      }, true);
    }

    // ── Lecture des params URL (?sc_gamme=...&sc_finition=...&sc_coloris=...) ──
    // Toutes les étapes restant toujours dépliées avec une valeur par défaut
    // (cf. selectGamme/selectFinition/selectColoris), ces params ne font que
    // remplacer après-coup la valeur par défaut par celle demandée dans l'URL.

    function initFromUrlParams() {
      var params = new URLSearchParams(window.location.search);
      var urlGamme    = params.get('sc_gamme');
      var urlFinition = params.get('sc_finition');
      var urlColoris  = params.get('sc_coloris');
      if (!urlGamme) return false;

      var gammeBtn2 = rootEl.querySelector('[data-sc-gamme="' + urlGamme + '"]');
      if (!gammeBtn2) return false;

      selectGamme(urlGamme, gammeBtn2.dataset.scGammeName, false);
      log('URL param → gamme:', urlGamme);

      if (!urlFinition) { save(); return true; }

      setTimeout(function() {
        var finBtn2 = rootEl.querySelector('[data-sc-finition="' + urlFinition + '"][data-sc-finition-gamme="' + urlGamme + '"]');
        if (!finBtn2) { save(); return; }
        selectFinition(urlFinition, finBtn2.dataset.scFinitionName, false);
        log('URL param → finition:', urlFinition);

        if (!urlColoris) { save(); return; }

        setTimeout(function() {
          var swatch = rootEl.querySelector('[data-sc-coloris="' + urlColoris + '"][data-sc-coloris-gamme="' + urlGamme + '"][data-sc-coloris-finition="' + urlFinition + '"]');
          if (swatch) {
            selectColoris(swatch, false);
            log('URL param → coloris:', urlColoris);
          }
          save();
        }, 50);
      }, 50);

      return true;
    }

    markCurrentForme();

    // ── Restauration de l'état ────────────────────────────────────────────
    if (opts.carryOver && opts.carryOver.gammeCode) {
      // Switch de forme : on réapplique Gamme/Finition/Coloris depuis le produit précédent.
      refreshUI();
      selectGamme(opts.carryOver.gammeCode, opts.carryOver.gammeNameVal, false);
      if (opts.carryOver.finitionCode) {
        setTimeout(function() {
          // Vérifie que la finition existe et est visible sur ce produit avant de la restaurer.
          // Si elle n'existe pas (ex. Cadre couleur sur Fileur), selectGamme a déjà
          // auto-sélectionné la première finition disponible — on ne l'écrase pas.
          var finBtn = rootEl.querySelector(
            '[data-sc-finition="' + opts.carryOver.finitionCode + '"]' +
            '[data-sc-finition-gamme="' + opts.carryOver.gammeCode + '"]'
          );
          if (finBtn && finBtn.style.display !== 'none') {
            selectFinition(opts.carryOver.finitionCode, opts.carryOver.finitionName, false);
            if (opts.carryOver.colorisCode) {
              setTimeout(function() {
                var swatch = rootEl.querySelector('[data-sc-coloris="' + opts.carryOver.colorisCode + '"]');
                if (swatch) selectColoris(swatch, false);
                if (opts.carryOver.optionCode) {
                  var optBtn = rootEl.querySelector('[data-sc-option="' + opts.carryOver.optionCode + '"]');
                  if (optBtn) selectOption(opts.carryOver.optionCode, opts.carryOver.optionName);
                }
                save();
              }, 50);
            } else {
              save();
            }
          } else {
            save();
          }
        }, 50);
      } else {
        save();
      }
    } else {
      refreshUI();
      var loadedFromUrl = initFromUrlParams();
      if (!loadedFromUrl) {
        restore();
        if (state.gammeCode) {
          var saved = {
            gammeCode: state.gammeCode, gammeNameVal: state.gammeNameVal,
            finitionCode: state.finitionCode, finitionName: state.finitionName,
            colorisCode: state.colorisCode,
            optionCode: state.optionCode, optionName: state.optionName,
            trancheDecorCode: state.trancheDecorCode, trancheDecorName: state.trancheDecorName,
          };
          selectGamme(saved.gammeCode, saved.gammeNameVal || saved.gammeCode, false);
          if (saved.finitionCode) {
            setTimeout(function() {
              selectFinition(saved.finitionCode, saved.finitionName || saved.finitionCode, false);
              if (saved.trancheDecorCode) {
                var trancheBtn = rootEl.querySelector('[data-sc-tranche="' + saved.trancheDecorCode + '"]');
                if (trancheBtn) selectTranche(saved.trancheDecorCode, saved.trancheDecorName);
              }
              if (saved.colorisCode) {
                setTimeout(function() {
                  var swatch = rootEl.querySelector('[data-sc-coloris="' + saved.colorisCode + '"]');
                  if (swatch) selectColoris(swatch, false);
                  if (saved.optionCode) {
                    var optBtn = rootEl.querySelector('[data-sc-option="' + saved.optionCode + '"]');
                    if (optBtn) selectOption(saved.optionCode, saved.optionName);
                  }
                  save();
                }, 50);
              } else {
                save();
              }
            }, 50);
          } else {
            save();
          }
        } else if (gammeBtns.length > 0) {
          // Pas de session ni de param URL : on atterrit par défaut sur la 1ère gamme.
          selectGamme(gammeBtns[0].dataset.scGamme, gammeBtns[0].dataset.scGammeName, true);
        }
      }
    }

    // ── Taille : mise à jour du header au clic sur le combo-box ─────────────
    rootEl.querySelectorAll('.combo-box__option-item').forEach(function(item) {
      item.addEventListener('click', function() {
        setStepValue('sc-step-taille-value', item.textContent.trim());
      });
    });

    // ── Galerie : navigation par thumbnails ──────────────────────────────────
    var galleryThumbs = rootEl.querySelector('#sc-gallery-thumbs');
    var galleryMain   = rootEl.querySelector('#sc-gallery-main');
    if (galleryThumbs && galleryMain) {
      // Un seul chemin pour activer une vue : setGalleryActive() sait alimenter
      // la diapositive de situation partagée. Ce gestionnaire dupliquait la
      // bascule des classes et laissait donc la vue vide depuis que les visuels
      // de situation ne sont plus rendus un par un.
      galleryThumbs.querySelectorAll('[data-sc-gallery-index]').forEach(function(thumb) {
        thumb.addEventListener('click', function() {
          setGalleryActive(thumb.dataset.scGalleryIndex);
        });
      });
    }

    current = { rootEl: rootEl, state: state, save: save, setStepValue: setStepValue, filterFinitionsByTaille: filterFinitionsByTaille, updateSkuAtelier: updateSkuAtelier, refreshOptionStep: refreshOptionStep, updateVariantForFinition: updateVariantForFinition, refreshFormatUI: refreshFormatUI };

    log('v' + SC_CONFIG.version + ' prêt', { productId: productId });
  }

  // Listener global unique (jamais ré-attaché) — délègue à l'instance courante,
  // qui change après chaque (ré)initialisation y compris un swap AJAX de forme.
  document.addEventListener('variant:changed', function(e) {
    if (!current) return;
    var v = e.detail && e.detail.variant;
    if (!v) return;

    var finitionMatches = !current.state.finitionName || !v.option2 ||
      v.option2.toLowerCase() === current.state.finitionName.toLowerCase();

    if (!finitionMatches) {
      // Focal a résolu la nouvelle taille sur une autre finition que celle choisie
      // (ex: pas de correspondance exacte) — on force le retour sur la finition
      // de l'utilisateur pour cette nouvelle taille plutôt que de figer l'état.
      if (v.option1 && current.state.finitionName && current.updateVariantForFinition) {
        current.updateVariantForFinition(current.state.finitionName, v.option1);
      }
      return;
    }

    var tailleChanged = v.option1 && v.option1 !== current.lastTaille;
    current.state.variantId = v.id;
    if (current.refreshFormatUI) current.refreshFormatUI();
    current.lastTaille = v.option1;
    current.setStepValue('sc-step-taille-value', v.option1 || '');
    if (tailleChanged && current.filterFinitionsByTaille) current.filterFinitionsByTaille();
    if (tailleChanged && current.refreshOptionStep) current.refreshOptionStep();
    if (current.updateSkuAtelier) current.updateSkuAtelier();
    current.save();

    if (v.price) {
      var newPrice = formatMoney(v.price);
      current.rootEl.querySelectorAll('[data-sc-price-top], [data-sc-price-bottom]').forEach(function(el) {
        el.textContent = newPrice;
      });
      var hasCompare = v.compare_at_price && v.compare_at_price > v.price;
      current.rootEl.querySelectorAll('[data-sc-compare-price-top], [data-sc-compare-price-bottom]').forEach(function(el) {
        el.style.display = hasCompare ? '' : 'none';
        if (hasCompare) el.textContent = formatMoney(v.compare_at_price);
      });
    }
  });

  document.addEventListener('DOMContentLoaded', function() {
    var root = document.getElementById('sc-configurator-root');
    if (root) initConfigurator(root);
  });

})();
